import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { getApiCapabilityBindings } from "@/lib/api/generated/capabilities/capabilities";
import { useGetApiIdentityRules } from "@/lib/api/generated/identity/identity";
import { getApiProviderIdentityLinks } from "@/lib/api/generated/capabilities/capabilities";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { queryKeys } from "@/lib/query/keys";
import { postApiIdentityUsers } from "@/lib/api/generated/identity/identity";
import { UsersSection } from "./users-section";
import { IdentityMappingsSection } from "./identity-mappings-section";
import { ProviderIdentityLinksSection } from "./provider-identity-links-section";
import { groupIdentityMappingsByUser } from "./identity-view-model";

export function IdentityRulesPage() {
  const { tenantId: activeTenantId } = useTenantContext();
  const {
    data: directoryUsers,
    isLoading: directoryUsersLoading,
    error: directoryUsersError,
  } = useQuery({
    queryKey: queryKeys.tenantDirectoryUsers(activeTenantId),
    queryFn: async () =>
      postApiIdentityUsers({
        tenantId: activeTenantId,
        page: 1,
        pageSize: 100,
      }),
    enabled: Boolean(activeTenantId),
  });
  const { data: identityRules, isLoading: rulesLoading } = useGetApiIdentityRules({
    query: { enabled: Boolean(activeTenantId) },
  });
  const { data: bindings, isLoading: bindingsLoading } = useQuery({
    queryKey: queryKeys.tenantCapabilityBindings(activeTenantId),
    queryFn: () => getApiCapabilityBindings({ tenantId: activeTenantId }),
    enabled: Boolean(activeTenantId),
  });
  const { data: providerLinks, isLoading: providerLinksLoading } = useQuery({
    queryKey: queryKeys.providerIdentityLinks(activeTenantId),
    queryFn: () => getApiProviderIdentityLinks({ tenantId: activeTenantId }),
    enabled: Boolean(activeTenantId),
  });

  useDocumentTitle("Users");

  const mappedUsers = useMemo(
    () => groupIdentityMappingsByUser(identityRules ?? []),
    [identityRules],
  );
  const delegatedBindings = useMemo(
    () =>
      (bindings ?? []).filter(
        (record) =>
          record.credentialMode === "delegated_user" ||
          record.credentialMode === "admin_impersonation",
      ),
    [bindings],
  );
  const providerOptions = useMemo(
    () => Array.from(new Set(delegatedBindings.map((record) => record.providerKey))).toSorted(),
    [delegatedBindings],
  );
  const readyLinkCount = useMemo(
    () =>
      (providerLinks ?? []).filter((record) => record.delegatedAuthState === "ready").length,
    [providerLinks],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
            Users
          </h2>
          <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
            These users are assigned to this tenant. Agentiq keeps identity mappings and provider links separately.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="Assigned users"
          value={String(directoryUsers?.items.length ?? 0)}
          description="Users currently assigned to Agentiq for this tenant."
        />
        <SummaryCard
          label="Agentiq mappings"
          value={String(mappedUsers.length)}
          description="Local Agentiq identity mappings layered on top of assigned users."
        />
        <SummaryCard
          label="Provider links"
          value={String(providerLinks?.length ?? 0)}
          description="Agentiq-local provider references for delegated execution."
        />
        <SummaryCard
          label="Delegated activations"
          value={String(delegatedBindings.length)}
          description={`Ready links: ${readyLinkCount}`}
        />
      </div>

      <div className="grid gap-6">
        <UsersSection
          tenantId={activeTenantId}
          users={directoryUsers?.items ?? []}
          isLoading={directoryUsersLoading}
          errorMessage={
            directoryUsersError instanceof Error ? directoryUsersError.message : undefined
          }
        />
        <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
          <IdentityMappingsSection
            tenantId={activeTenantId}
            rules={identityRules ?? []}
            isLoading={rulesLoading}
          />
          <ProviderIdentityLinksSection
            tenantId={activeTenantId}
            users={directoryUsers?.items ?? []}
            mappedUsers={mappedUsers}
            providerLinks={providerLinks ?? []}
            providerOptions={providerOptions}
            isLoading={providerLinksLoading || bindingsLoading}
          />
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) {
  return (
    <div
      className="rounded-md border px-4 py-4"
      style={{ borderColor: "var(--border)", background: "var(--panel)" }}
    >
      <div
        className="text-[11px] font-semibold uppercase tracking-[0.14em]"
        style={{ color: "var(--muted-foreground)" }}
      >
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold" style={{ color: "var(--text-strong)" }}>
        {value}
      </div>
      <p className="mt-1 text-xs" style={{ color: "var(--muted-foreground)" }}>
        {description}
      </p>
    </div>
  );
}
